// Copyright ©️ 2026 eVolve MEP, LLC

import './file-uploads.css';

import React, { type ReactNode, useEffect, useState } from 'react';

import type { CreateWebLinkBody, StrongOmit } from '@evolvemep/api-ts';
import {
  Stack,
  InputLabel,
  InputDescription,
  FileButton,
  Text,
  Menu,
  ActionIcon,
  Flex,
  useMantineTheme,
  Card,
  Overlay,
  type CardProps,
} from '@mantine/core';
import { Dropzone, type DropzoneProps } from '@mantine/dropzone';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AddWebLinkPopover } from 'components/Mantine/Attachments/AddWebLinkPopover';
import { FILE_TYPE_ACCEPT } from 'components/Mantine/FileUploads/useUploadFiles';
import { useModalOpenGuard } from 'components/Mantine/Modals/useModalOpenGuard';
import { isNil } from 'helpers/isNotNil';

type UploadHandlerProps =
  | {
      onAddFiles: (files: File[]) => Promise<void>;
      onAddWebLink: (webLink: CreateWebLinkBody) => Promise<void>;
    }
  /** Omit both handlers to render a read-only view. */
  | {
      onAddFiles?: never;
      onAddWebLink?: never;
    };

type Props = {
  label?: string;
  description?: string;
  required?: boolean;
  children: ReactNode;
  /** If true, will render a box instructing the user how to upload */
  showUploadBox?: boolean;
  uploadBoxProps?: CardProps;
  /** If true, will render children inside the upload box */
  renderChildrenInUploadBox?: boolean;
} & UploadHandlerProps &
  StrongOmit<DropzoneProps, 'onDrop'>;

export const DragNDropFilesMessage = ({ more }: { more?: boolean }) => (
  <Flex c="dimmed" gap={8} align="center" mt={more ? 'xs' : 0}>
    <EvolveIcon icon="Upload" size="sm" />
    <Text fz="small">Drag and drop files here or click to select {more ? 'more' : ''} files.</Text>
  </Flex>
);

/**
 * Generic wrapper for uploading files or adding web links.
 * The uploaded attachments are expected to be rendered via `children`
 */
export const FileUploads = ({
  label = 'Upload Files',
  description,
  required,
  showUploadBox,
  uploadBoxProps,
  renderChildrenInUploadBox,
  onAddFiles,
  onAddWebLink,
  disabled: parentDisabled,
  children,
  ...props
}: Props) => {
  const theme = useMantineTheme();
  const [addingLink, setAddingLink] = useState(false);
  const [addMenuOpened, setAddMenuOpened] = useState(false);
  const modalOpenGuard = useModalOpenGuard();

  const disabled = parentDisabled || isNil(onAddFiles);

  useEffect(() => {
    if (!addMenuOpened) return undefined;
    return modalOpenGuard?.register();
  }, [addMenuOpened, modalOpenGuard]);

  return (
    <Dropzone
      onDrop={(files) => onAddFiles?.(files)}
      accept={FILE_TYPE_ACCEPT}
      multiple
      disabled={disabled}
      activateOnKeyboard={false}
      activateOnClick={false}
      styles={{
        root: {
          border: 'none',
          borderRadius: 10,
          backgroundColor: 'transparent',
          overflow: 'hidden',
          flex: 0,
        },
        inner: { pointerEvents: 'all' },
      }}
      // Add padding (and then hide it with margin)
      // so that you only see the full size of the dropzone
      // when dragging and dropping files
      p="var(--mantine-spacing-xs, 0)"
      m="calc(-1 * var(--mantine-spacing-xs, 0))"
      {...props}
    >
      {!disabled && (
        <>
          <Dropzone.Accept>
            <Overlay bg="rgb(from var(--mantine-primary-color-1) r g b / 75%)">
              <Flex gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                <EvolveIcon icon="Upload" />
                <Text fw={500}>Drag to upload</Text>
              </Flex>
            </Overlay>
          </Dropzone.Accept>
          <Dropzone.Reject>
            <Overlay bg="rgb(from var(--mantine-color-red-3) r g b / 75%)">
              <Flex gap="sm" align="center" justify="center" style={{ height: '100%' }}>
                <EvolveIcon icon="Close" />
                <Text fw={500}>Cannot accept files</Text>
              </Flex>
            </Overlay>
          </Dropzone.Reject>
        </>
      )}

      <FileButton
        onChange={(files) => onAddFiles?.(files)}
        accept={FILE_TYPE_ACCEPT.join(',')}
        multiple
        disabled={disabled}
      >
        {(fileButtonProps) => (
          <Stack gap={2}>
            <Flex gap="xs" align="center">
              <InputLabel required={required}>{label}</InputLabel>
              {!disabled && (
                <>
                  <Menu position="bottom-start" opened={addMenuOpened} onChange={setAddMenuOpened}>
                    <Menu.Target>
                      <ActionIcon variant="light" size="xs" color={theme.primaryColor}>
                        <EvolveIcon icon="Add" size="xs" />
                      </ActionIcon>
                    </Menu.Target>

                    <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                      <Menu.Item leftSection={<EvolveIcon icon="Attachment" />} {...fileButtonProps}>
                        Attach files
                      </Menu.Item>
                      <Menu.Item onClick={() => setAddingLink(true)} leftSection={<EvolveIcon icon="External" />}>
                        Add web link
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>

                  <AddWebLinkPopover
                    opened={addingLink}
                    onClose={() => setAddingLink(false)}
                    onAddWebLink={async (v) => {
                      await onAddWebLink?.(v);
                    }}
                  />
                </>
              )}
            </Flex>

            {!!description && <InputDescription>{description}</InputDescription>}

            {!renderChildrenInUploadBox && children}

            {(showUploadBox || renderChildrenInUploadBox) && (
              <Card
                withBorder
                className={disabled ? undefined : 'file-upload-box'}
                py={disabled ? 4 : 'xs'}
                px="sm"
                style={{ borderRadius: 10 }}
                {...uploadBoxProps}
                {...fileButtonProps}
              >
                {renderChildrenInUploadBox && children}
                {showUploadBox &&
                  (!disabled ? (
                    <DragNDropFilesMessage />
                  ) : (
                    <Text size="xs" c="dimmed">
                      No attachments.
                    </Text>
                  ))}
              </Card>
            )}
          </Stack>
        )}
      </FileButton>
    </Dropzone>
  );
};
